import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// local imports && assets
import successImage from 'assets/images/success-image.svg'
import calendarLoading from 'assets/icons/loading-calendar.png'
import useDidMountEffect from 'utils/useDidMountEffect'
import AuthUtil from 'utils/AuthUtil'
import DateUtil from 'utils/DateUtil'
import { IFilteredLactationRoomList, ILactationRoomFilteredReq } from 'types/LactationType'
import Loader from 'components/ui/Loader'
import NoContent from 'components/ui/NoContent'
import NotificationDialog from 'components/ui/NotificationDialog'
import useLactationRoomResModal from '../hooks/use-lactation-room-res-modal'
import useGetLactationLocationList from '../hooks/use-get-lactation-room-locations'
import useGetLactationRoomList from '../hooks/use-get-lactation-room-list'
import LactationRoomSearch from './LactatitonRoomSearch'
import LactationRoomCard from './LactationRoomCard'
import LactationRoomFilterForm from './LactationRoomFilterForm'
import ReservationModalWrapper from '../reservation-scheduler/ReservationModal'
import { Divider, FormLayoutWrapper, Line, RoomCardList } from './styled'

/* filtered date - default */
const today = DateUtil.getStartOfDay(new Date())

export default function LactationRoomList() {
  const history = useHistory()
  const { t } = useTranslation()
  const [locationIds, setLocationIds] = useState<number[] | []>([])

  /* Get Current logged employee infos - (location id) */
  const { data: locationList, isLoading } = useGetLactationLocationList()

  useEffect(() => {
    const employeeLocation = AuthUtil.getEmployeeSummary()?.locationId || 0
    const empLocationIsExist = locationList?.map((o: any) => o.id).includes(employeeLocation)
    setLocationIds(() => (empLocationIsExist ? [employeeLocation] : []))
    setValue('locationIds', empLocationIsExist ? [employeeLocation] : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationList])

  /* Filter form default values */
  const defaultValues: ILactationRoomFilteredReq = { locationIds: locationIds, reservationDate: today }

  /*  Search and filter from Lactation Room list */
  const [searchText, setSearchText] = useState('')
  const [filteredRoom, setFilteredRoom] = useState<IFilteredLactationRoomList[] | []>([])

  /* Filter form state  */
  const { control, reset, handleSubmit, formState, getValues, setValue, watch } = useForm<ILactationRoomFilteredReq>({
    defaultValues,
  })
  const filteredDate = watch('reservationDate') || today

  /* Get Lactation Room List - Locations | Custom Hooks */
  const { getLactationRoomList, lactationRooms, loading } = useGetLactationRoomList()

  /* Form Actions | Methods | button disabled states */
  const { isDirty, submitCount } = formState
  const disableResetBtn = !isDirty && submitCount < 1
  const disableSubmitBtn = !isDirty && submitCount < 1

  /* Handle Form Submit */
  const onSubmit = (data: ILactationRoomFilteredReq) => getLactationRoomList(data)
  const runAfterSubmit = () => getLactationRoomList(getValues())

  /* Handle Reset Filter Form */
  const handleResetForm = () => {
    reset(
      { locationIds: locationIds, reservationDate: today },
      { keepDirtyValues: false, keepDirty: false, keepDefaultValues: false }
    )
    !!submitCount && onSubmit(defaultValues)
    setSearchText('')
  }

  /* Get Lactation Room List on initial page load  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useDidMountEffect(() => onSubmit(defaultValues), [isLoading])

  /* Render lactationRooms or filtered Lactation Room list */
  const renderList = searchText ? filteredRoom : lactationRooms

  const {
    modal: isOpen,
    openModal,
    closeModal,
    notification,
    setNotification,
    selectedRoom,
  } = useLactationRoomResModal()

  const closeNotification = () => {
    runAfterSubmit()
    setNotification(false)
  }

  /* Show full-screen loader only on initial load (when list is empty).
     During background refresh after a reservation, keep cards mounted so the
     notification dialog can appear and counts update in place. */
  if ((loading || isLoading) && !lactationRooms.length) return <ListLoader />

  return (
    <div className="campus-services">
      {/* Filter Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLayoutWrapper>
          <LactationRoomSearch
            rooms={lactationRooms}
            setData={setFilteredRoom}
            searchText={searchText}
            setSearchText={setSearchText}
          />

          <Line className="pi pi-ellipsis-h" />
          <LactationRoomFilterForm
            control={control}
            locationList={locationList}
            disableResetBtn={disableResetBtn}
            disableSubmitBtn={disableSubmitBtn}
            handleReset={handleResetForm}
          />
        </FormLayoutWrapper>
      </form>

      <Divider />

      {/* Card List — keep mounted during refresh so the success notification
          dialog (rendered below) is not unmounted. */}
      <RoomCardList>
        {!!renderList?.length ? (
          renderList?.map((room) => <LactationRoomCard openModal={openModal} key={room.id} room={room} />)
        ) : (
          <NoContent />
        )}
      </RoomCardList>

      {/* Lactation Room Reservation Scheduler Modal */}
      {!!selectedRoom ? (
        <ReservationModalWrapper
          open={isOpen}
          openModal={openModal}
          closeModal={closeModal}
          showNotification={() => {
            runAfterSubmit()
            setNotification(true)
          }}
          selectedRoom={selectedRoom}
          filterDate={filteredDate}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('lactation-res-modal.notification-title')}
        content={t('lactation-res-modal.notification-text')}
        firstButtonLabel={t('buttons.go-to-calendar')}
        onHide={closeNotification}
        onSubmit={() => history.push('/calendar')}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </div>
  )
}

function ListLoader() {
  return (
    <div className="py-5 my-4">
      <Loader src={calendarLoading} />
    </div>
  )
}
